import { replay } from '@ctd/core';
import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { useNotify } from '../components/notices';
import { PageHeading, TableRegion } from '../components/ui';
import { useRepo } from '../context';
import { downloadText, errorText } from '../lib/download';
import {
  duplicateEvents,
  exportJson,
  exportTrf,
  fileBaseName,
  importTrf,
  looksLikeTrf,
  parseTournamentFile,
  withNewId,
} from '../lib/files';
import { recordFromEvents, type TournamentRecord } from '../lib/repo';
import { hrefFor, navigate } from '../lib/router';
import { engineOptions } from '../lib/settings';

function systemName(id: string): string {
  return engineOptions().find((e) => e.id === id)?.name ?? id;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function ListPage() {
  const repo = useRepo();
  const notify = useNotify();
  const [records, setRecords] = useState<TournamentRecord[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    document.title = 'Tournaments · Chess TD';
  }, []);

  useEffect(() => {
    let cancelled = false;
    repo.list().then(
      (list) => {
        if (!cancelled) setRecords(list);
      },
      (e: unknown) => {
        if (!cancelled) setLoadError(errorText(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [repo, version]);

  async function onImport(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const input = e.currentTarget;
    const file = input.files?.[0];
    input.value = '';
    if (file === undefined) return;
    try {
      const text = await file.text();
      let events;
      let warnings: string[] = [];
      if (looksLikeTrf(text) || /\.(trf|txt)$/i.test(file.name)) {
        ({ events, warnings } = importTrf(text));
      } else {
        events = parseTournamentFile(text);
      }
      const t = replay(events);
      let copy = false;
      if ((await repo.get(t.id)) !== undefined) {
        events = withNewId(events);
        copy = true;
      }
      const record = recordFromEvents(events);
      await repo.put(record);
      reload();
      const extra = [
        copy ? 'a tournament with the same id exists, so it was imported as a copy' : '',
      ]
        .concat(warnings)
        .filter((s) => s !== '');
      notify(`Imported "${record.name}"${extra.length > 0 ? `: ${extra.join('; ')}` : ''}.`);
      navigate({ name: 'tournament', id: record.id, tab: 'players' });
    } catch (err) {
      notify(`Could not import ${file.name}: ${errorText(err)}`, 'error');
    }
  }

  async function onDuplicate(r: TournamentRecord): Promise<void> {
    try {
      const record = recordFromEvents(duplicateEvents(r.events));
      await repo.put(record);
      reload();
      notify(`Created "${record.name}".`);
    } catch (e) {
      notify(`Could not duplicate: ${errorText(e)}`, 'error');
    }
  }

  async function onDelete(r: TournamentRecord): Promise<void> {
    const ok = window.confirm(
      `Delete "${r.name}"? This cannot be undone. Export it first if you may need it again.`,
    );
    if (!ok) return;
    try {
      await repo.delete(r.id);
      reload();
      notify(`Deleted "${r.name}".`);
    } catch (e) {
      notify(`Could not delete: ${errorText(e)}`, 'error');
    }
  }

  function onExportJson(r: TournamentRecord): void {
    downloadText(`${fileBaseName(r.name)}.ctd.json`, exportJson(r.events), 'application/json');
  }

  function onExportTrf(r: TournamentRecord): void {
    try {
      downloadText(`${fileBaseName(r.name)}.trf`, exportTrf(replay(r.events)), 'text/plain');
    } catch (e) {
      notify(`Could not export TRF: ${errorText(e)}`, 'error');
    }
  }

  return (
    <section>
      <div className="page-head">
        <PageHeading level={1}>Tournaments</PageHeading>
        <div className="actions">
          <a className="button primary" href={hrefFor({ name: 'new' })}>
            New tournament
          </a>
          <label className="button file-button">
            Import file…
            <input
              type="file"
              className="visually-hidden"
              accept=".json,.trf,.txt,application/json,text/plain"
              onChange={(e) => void onImport(e)}
            />
          </label>
        </div>
      </div>
      <p className="lede">
        Tournaments are kept in this browser, and the app works without a connection. Import a Chess
        TD file (.json) or a FIDE TRF file; export to keep a backup or move to another device.
      </p>
      {loadError !== null && (
        <p className="error-box" role="alert">
          Could not read the saved tournaments: {loadError}
        </p>
      )}
      {records === null && loadError === null && <p>Loading…</p>}
      {records !== null && records.length === 0 && (
        <div className="empty">
          <p>No tournaments yet.</p>
          <p>
            <a href={hrefFor({ name: 'new' })}>Create your first tournament</a> or import a file.
          </p>
        </div>
      )}
      {records !== null && records.length > 0 && (
        <TableRegion label="Saved tournaments">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Start</th>
                <th scope="col">System</th>
                <th scope="col" className="num">
                  Players
                </th>
                <th scope="col" className="num">
                  Rounds
                </th>
                <th scope="col">Last change</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <th scope="row">
                    <a href={hrefFor({ name: 'tournament', id: r.id, tab: 'players' })}>{r.name}</a>
                  </th>
                  <td>{r.startDate || '–'}</td>
                  <td>{systemName(r.system)}</td>
                  <td className="num">{r.playerCount}</td>
                  <td className="num">
                    {r.roundsPaired} / {r.totalRounds}
                  </td>
                  <td>{formatWhen(r.updatedAt)}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="button small"
                        aria-label={`Duplicate ${r.name}`}
                        onClick={() => void onDuplicate(r)}
                      >
                        Duplicate
                      </button>
                      <button
                        type="button"
                        className="button small"
                        aria-label={`Export ${r.name} as JSON`}
                        onClick={() => onExportJson(r)}
                      >
                        Export JSON
                      </button>
                      <button
                        type="button"
                        className="button small"
                        aria-label={`Export ${r.name} as TRF`}
                        onClick={() => onExportTrf(r)}
                      >
                        Export TRF
                      </button>
                      <button
                        type="button"
                        className="button small danger"
                        aria-label={`Delete ${r.name}`}
                        onClick={() => void onDelete(r)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableRegion>
      )}
    </section>
  );
}
